// frontend/src/components/Landing.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const HEADER_IMG = '/bracketsim_header.png';
const SCENARIO_IMG = '/scenario.webp';
const POOL_IMG = '/standings.webp';
const PROBABILITY_IMG = '/probability.webp';

const BracketGraphic: React.FC = () => (
  <svg viewBox="0 0 400 300" style={s.bracketSvg}>
    {/* Left side bracket lines */}
    <g stroke="#78716c" strokeWidth="2" fill="none" opacity="0.45">
      <line x1="20" y1="30" x2="60" y2="30" />
      <line x1="20" y1="60" x2="60" y2="60" />
      <line x1="60" y1="30" x2="60" y2="60" />
      <line x1="60" y1="45" x2="100" y2="45" />

      <line x1="20" y1="90" x2="60" y2="90" />
      <line x1="20" y1="120" x2="60" y2="120" />
      <line x1="60" y1="90" x2="60" y2="120" />
      <line x1="60" y1="105" x2="100" y2="105" />

      <line x1="20" y1="150" x2="60" y2="150" />
      <line x1="20" y1="180" x2="60" y2="180" />
      <line x1="60" y1="150" x2="60" y2="180" />
      <line x1="60" y1="165" x2="100" y2="165" />

      <line x1="20" y1="210" x2="60" y2="210" />
      <line x1="20" y1="240" x2="60" y2="240" />
      <line x1="60" y1="210" x2="60" y2="240" />
      <line x1="60" y1="225" x2="100" y2="225" />

      <line x1="100" y1="45" x2="100" y2="105" />
      <line x1="100" y1="75" x2="140" y2="75" />

      <line x1="100" y1="165" x2="100" y2="225" />
      <line x1="100" y1="195" x2="140" y2="195" />

      <line x1="140" y1="75" x2="140" y2="195" />
      <line x1="140" y1="135" x2="180" y2="135" />
    </g>

    {/* Right side bracket lines */}
    <g stroke="#78716c" strokeWidth="2" fill="none" opacity="0.45">
      <line x1="380" y1="30" x2="340" y2="30" />
      <line x1="380" y1="60" x2="340" y2="60" />
      <line x1="340" y1="30" x2="340" y2="60" />
      <line x1="340" y1="45" x2="300" y2="45" />

      <line x1="380" y1="90" x2="340" y2="90" />
      <line x1="380" y1="120" x2="340" y2="120" />
      <line x1="340" y1="90" x2="340" y2="120" />
      <line x1="340" y1="105" x2="300" y2="105" />

      <line x1="380" y1="150" x2="340" y2="150" />
      <line x1="380" y1="180" x2="340" y2="180" />
      <line x1="340" y1="150" x2="340" y2="180" />
      <line x1="340" y1="165" x2="300" y2="165" />

      <line x1="380" y1="210" x2="340" y2="210" />
      <line x1="380" y1="240" x2="340" y2="240" />
      <line x1="340" y1="210" x2="340" y2="240" />
      <line x1="340" y1="225" x2="300" y2="225" />

      <line x1="300" y1="45" x2="300" y2="105" />
      <line x1="300" y1="75" x2="260" y2="75" />

      <line x1="300" y1="165" x2="300" y2="225" />
      <line x1="300" y1="195" x2="260" y2="195" />

      <line x1="260" y1="75" x2="260" y2="195" />
      <line x1="260" y1="135" x2="220" y2="135" />
    </g>

    {/* Championship */}
    <g stroke="#e8622d" strokeWidth="3" fill="none">
      <line x1="180" y1="135" x2="200" y2="135" />
      <line x1="220" y1="135" x2="200" y2="135" />
      <circle cx="200" cy="135" r="15" fill="#e8622d" opacity="0.25" />
      <text x="200" y="140" textAnchor="middle" fill="#e8622d" fontSize="12" fontWeight="bold">?</text>
    </g>

    {/* Animated pulse */}
    <circle cx="200" cy="135" r="25" fill="none" stroke="#e8622d" strokeWidth="2" opacity="0.4">
      <animate attributeName="r" values="25;35;25" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const Landing: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({ path: '/' });

  const handleStartSimulating = () => navigate('/login');

  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.topNav}>
        <a href="/login" className="landing-login-link" style={s.navAccent}>Log In</a>
      </nav>

      {/* Hero */}
      <section style={s.hero} className="landing-hero">
        {/* Big centered logo */}
        <div style={s.heroLogoWrap}>
          <img src={HEADER_IMG} alt="BracketSim" style={s.heroLogo} />
        </div>

        <div style={s.heroInner}>
          <div style={s.heroContent}>
            <h1 style={s.headline}>
              9.2 Quintillion Tournament Possibilities.
              <br />
              <span style={s.headlineAccent}>One Clear Answer.</span>
            </h1>
            <p style={s.subheadline}>
              Know exactly where you stand in your bracket pool and which games actually matter
            </p>
            <button
              style={s.ctaBtn}
              className="landing-cta-button"
              onClick={handleStartSimulating}
            >
              Start Simulating
            </button>
          </div>
          <div style={s.heroGraphic}>
            <BracketGraphic />
          </div>
        </div>
      </section>

      {/* Product Hunt Badge */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0 0' }}>
        <a href="https://www.producthunt.com/products/my-bracket-sim?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-my-bracket-sim" target="_blank" rel="noopener noreferrer">
          <img alt="My Bracket Sim - NCAA Bracket Scenario Generator and Simulator | Product Hunt" width="250" height="54" src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1102866&theme=light&t=1773976780292" />
        </a>
      </div>

      {/* Problem/Solution Cards */}
      <section style={s.cardsSection}>
        <div style={s.cardsGrid}>
          <div style={s.card}>
            <h3 style={s.cardProblem}>"Is my bracket toast?"</h3>
            <h4 style={s.cardSolution}>Scenario Builder</h4>
            <p style={s.cardDesc}>
              Create what-if scenarios to see who wins your pool under different tournament outcomes
            </p>
          </div>
          <div style={s.card}>
            <h3 style={s.cardProblem}>"What are my actual odds?"</h3>
            <h4 style={s.cardSolution}>Advanced Simulator</h4>
            <p style={s.cardDesc}>
              Run Monte Carlo simulations using live real-time odds for accurate probability distributions
            </p>
          </div>
          <div style={s.card}>
            <h3 style={s.cardProblem}>"Which games should I watch?"</h3>
            <h4 style={s.cardSolution}>Rooting Interest Finder</h4>
            <p style={s.cardDesc}>
              See exactly which games matter for you or anyone in your pool
            </p>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section style={s.featuresSection}>
        <div style={s.feature}>
          <div style={s.featureContent}>
            <h2 style={s.featureTitle}>Scenario Generator</h2>
            <ul style={s.featureList} className="landing-feature-list">
              <li>Build unlimited what-if scenarios</li>
              <li>Test different tournament outcomes instantly</li>
              <li>Simulate 9.2 quintillion possible bracket combinations</li>
            </ul>
          </div>
          <div style={s.featurePlaceholder}>
            <img
              src={SCENARIO_IMG}
              alt="Scenario Interface"
              style={{ ...s.featureImage, maxWidth: '250px' }}
            />
          </div>
        </div>

        <div style={{ ...s.feature, ...s.featureReverse }}>
          <div style={s.featureContent}>
            <h2 style={s.featureTitle}>Pool Management</h2>
            <ul style={s.featureList} className="landing-feature-list">
              <li>Track multiple pools in one place</li>
              <li>Customize your scoring by round</li>
              <li>Automatically import your existing pool<br />from ESPN, CBS, or Yahoo Bracket Games</li>
            </ul>
          </div>
          <div style={s.featurePlaceholder}>
            <img src={POOL_IMG} alt="Pool Standings" style={s.featureImage} />
          </div>
        </div>

        <div style={s.feature}>
          <div style={s.featureContent}>
            <h2 style={s.featureTitle}>Live Odds Integration</h2>
            <ul style={s.featureList} className="landing-feature-list">
              <li>Powered by real-time odds</li>
              <li>Statistical projections you can trust</li>
              <li>Updated throughout the tournament</li>
            </ul>
          </div>
          <div style={s.featurePlaceholder}>
            <img src={PROBABILITY_IMG} alt="Probability Chart" style={s.featureImage} />
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section style={s.socialProof}>
        <p style={s.socialProofText}>
          Be the 6th man of your bracket pool and know everything about your pool before it happens.
        </p>
      </section>

      {/* Footer CTA */}
      <section style={s.footerCta}>
        <h2 style={s.footerHeadline}>Ready to know your odds?</h2>
        <button
          style={s.ctaBtn}
          className="landing-cta-button"
          onClick={handleStartSimulating}
        >
          Start Simulating
        </button>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <p style={s.footerRow}>
          <a href="/privacy" className="landing-flink">Privacy Policy</a>
          <span style={s.dot}>&middot;</span>
          <a href="/contact" className="landing-flink">Contact</a>
          <span style={s.dot}>&middot;</span>
          <a href="/blog/" className="landing-flink">Blog</a>
        </p>
        <p style={{ marginBottom: '12px' }}>
          <a href="mailto:hello@mybracketsim.com" className="landing-flink">
            hello@mybracketsim.com
          </a>
        </p>
        <p style={s.copy}>&copy; 2026 MyBracketSim</p>
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
  },

  /* ── Top Nav ── */
  topNav: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '18px 8%',
  },
  navLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#1c1917',
    textDecoration: 'none',
    letterSpacing: '0.03em',
  },
  navAccent: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#e8622d',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    padding: '7px 18px',
    border: '1.5px solid #e8622d',
    borderRadius: '3px',
  },

  /* ── Hero ── */
  hero: {
    padding: '20px 8% 60px',
  },
  heroLogoWrap: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '48px',
  },
  heroLogo: {
    maxWidth: '500px',
    width: '100%',
    height: 'auto',
  },
  heroInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1100px',
    margin: '0 auto',
    flexWrap: 'wrap',
    gap: '40px',
  },
  heroContent: {
    flex: '1 1 480px',
    maxWidth: '600px',
  },
  headline: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.6rem, 3.5vw, 2.6rem)',
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: '20px',
    marginTop: 0,
    color: '#1c1917',
  },
  headlineAccent: {
    color: '#ffffff',
    backgroundColor: '#e8622d',
    padding: '4px 14px',
    borderRadius: '2px',
    display: 'inline-block',
  },
  subheadline: {
    fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
    color: '#57534e',
    marginBottom: '32px',
    lineHeight: 1.6,
  },
  ctaBtn: {
    fontFamily: '"Space Mono", monospace',
    padding: '14px 36px',
    fontSize: '1rem',
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: '#e8622d',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },
  heroGraphic: {
    flex: '1 1 350px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bracketSvg: {
    width: '100%',
    maxWidth: '440px',
    height: 'auto',
  },

  /* ── Cards ── */
  cardsSection: {
    padding: '70px 8%',
    borderTop: '1px solid #e7e5e4',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  card: {
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e7e5e4',
    borderRight: '1px solid #e7e5e4',
    borderBottom: '1px solid #e7e5e4',
    borderLeft: '3px solid #e8622d',
    padding: '32px 28px',
  },
  cardProblem: {
    fontSize: '1.05rem',
    color: '#78716c',
    fontStyle: 'italic',
    marginTop: 0,
    marginBottom: '12px',
    fontWeight: 400,
  },
  cardSolution: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#e8622d',
    marginTop: 0,
    marginBottom: '10px',
  },
  cardDesc: {
    fontSize: '0.95rem',
    color: '#57534e',
    lineHeight: 1.65,
    margin: 0,
  },

  /* ── Features ── */
  featuresSection: {
    padding: '70px 8%',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '48px',
    marginBottom: '70px',
    flexWrap: 'wrap',
  },
  featureReverse: {
    flexDirection: 'row-reverse',
  },
  featureContent: {
    flex: '1 1 380px',
  },
  featureTitle: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.4rem',
    fontWeight: 700,
    marginBottom: '20px',
    marginTop: 0,
    color: '#e8622d',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featurePlaceholder: {
    flex: '1 1 380px',
    display: 'flex',
    justifyContent: 'center',
  },
  featureImage: {
    width: '100%',
    maxWidth: '500px',
    height: 'auto',
    borderRadius: '4px',
    boxShadow: '0 8px 30px -8px rgba(28, 25, 23, 0.18)',
  },

  /* ── Social Proof ── */
  socialProof: {
    padding: '50px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
    borderBottom: '1px solid #e7e5e4',
  },
  socialProofText: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
    color: '#57534e',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: 1.6,
    fontStyle: 'italic',
  },

  /* ── Footer CTA ── */
  footerCta: {
    padding: '70px 8%',
    textAlign: 'center',
  },
  footerHeadline: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)',
    fontWeight: 700,
    marginTop: 0,
    marginBottom: '24px',
    color: '#1c1917',
  },

  /* ── Footer ── */
  footer: {
    padding: '36px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  footerRow: { marginBottom: '10px' },
  dot: { margin: '0 10px', color: '#a8a29e' },
  copy: { color: '#a8a29e', fontSize: '0.8rem', margin: 0 },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction & layout styles */
if (!document.querySelector('#landing-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'landing-styles';
  sheet.textContent = `
    .landing-hero {
      background-image: radial-gradient(circle, rgba(28, 25, 23, 0.045) 1px, transparent 1px);
      background-size: 32px 32px;
    }
    .landing-feature-list li {
      padding: 7px 0;
      padding-left: 26px;
      position: relative;
      color: #57534e;
      font-size: 1rem;
      line-height: 1.7;
    }
    .landing-feature-list li::before {
      content: "\\2192";
      position: absolute;
      left: 0;
      color: #e8622d;
      font-family: "Space Mono", monospace;
      font-weight: 700;
    }
    .landing-cta-button:hover {
      background-color: #c2410c !important;
    }
    .landing-login-link:hover {
      background-color: #e8622d !important;
      color: #fff !important;
    }
    .landing-flink {
      color: #78716c;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .landing-flink:hover { color: #e8622d; }
  `;
  document.head.appendChild(sheet);
}

export default Landing;
